import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Lock, IdCard, ShieldCheck, AlertCircle, User } from "lucide-react";

const REQUIRED_USER_FIELDS = [
  { key: "name", labelIt: "Nome", labelEn: "First name" },
  { key: "surname", labelIt: "Cognome", labelEn: "Last name" },
  { key: "document_number", labelIt: "N. Documento", labelEn: "Document number" },
  { key: "birth_date", labelIt: "Data di nascita", labelEn: "Date of birth", type: "date" },
  { key: "birth_place", labelIt: "Luogo di nascita", labelEn: "Place of birth" },
  { key: "phone", labelIt: "Telefono", labelEn: "Phone", type: "tel" },
  { key: "address_street", labelIt: "Via", labelEn: "Street", colSpan: 2 },
  { key: "address_city", labelIt: "Città", labelEn: "City" },
  { key: "address_zip", labelIt: "CAP", labelEn: "ZIP" },
];

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [profile, setProfile] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [acceptedNoRefund, setAcceptedNoRefund] = useState(false);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    api.get("/payments/maintenance").then((r) => setMaintenance(!!r.data?.maintenance)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/bookings/mine").then((r) => {
      const b = (r.data || []).find((x) => x.id === bookingId);
      setBooking(b || false);
    });
  }, [bookingId]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setProfile({
        name: user.name || "",
        surname: user.surname || "",
        document_number: user.document_number || "",
        birth_date: user.birth_date || "",
        birth_place: user.birth_place || "",
        phone: user.phone || "",
        address_street: user.address_street || "",
        address_city: user.address_city || "",
        address_zip: user.address_zip || "",
      });
    });
    return () => { cancelled = true; };
  }, [user]);

  const missing = REQUIRED_USER_FIELDS.filter((f) => !String(profile[f.key] || "").trim());
  const profileComplete = missing.length === 0;

  const saveProfile = async () => {
    if (missing.length > 0) {
      toast.error(isEN ? "Please fill all required fields" : "Compila tutti i campi obbligatori");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", profile);
      await refresh();
      toast.success(isEN ? "Profile updated" : "Profilo aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const pay = async () => {
    if (!profileComplete) {
      toast.error(isEN ? "Complete your profile before paying" : "Completa il profilo prima di pagare");
      return;
    }
    if (!accepted) {
      toast.error(isEN ? "You must accept the rental contract" : "Devi accettare il contratto");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(`/payments/checkout/${bookingId}`, { origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  if (booking === null) return <div className="text-zinc-400">{t("common.loading")}</div>;
  if (!booking) return <div className="text-zinc-400">{t("checkout.not_found")}</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="font-display text-4xl font-bold tracking-tight">{t("checkout.title")}</h1>

      {/* Booking summary */}
      <div className="border border-zinc-800 rounded-xl p-6 bg-zinc-900/40 space-y-3">
        <Row label={t("checkout.scooter")} value={booking.scooter_model} />
        <Row label={t("checkout.plate")} value={booking.scooter_plate} />
        <Row label={t("checkout.hours")} value={booking.days || booking.hours || 0} />
        <Row label={t("checkout.start")} value={new Date(booking.start_at).toLocaleString()} small />
        <Row label={t("checkout.end")} value={new Date(booking.end_at).toLocaleString()} small />
        <div className="border-t border-zinc-800 pt-4 space-y-2">
          <div className="flex items-center justify-between text-sm text-zinc-400">
            <span>{isEN ? "Total rental" : "Totale noleggio"}</span>
            <span data-testid="checkout-total">€{booking.total_amount.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-display font-bold text-base text-[#22c55e] flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" />
              {isEN ? "Deposit due now (25%)" : "Caparra da versare ora (25%)"}
            </span>
            <span className="font-display font-bold text-2xl text-[#22c55e]" data-testid="checkout-caparra">
              €{(booking.caparra_amount ?? booking.total_amount * 0.25).toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-300">{isEN ? "Balance on-site" : "Saldo in sede"}</span>
            <span className="text-zinc-100 font-semibold" data-testid="checkout-balance-onsite">
              €{(booking.balance_due_on_site ?? booking.total_amount * 0.75).toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed pt-2">
            {isEN
              ? "By paying the deposit you confirm the booking. The remaining balance will be due in cash or card when you pick up the vehicle at our office in Marciana Marina."
              : "Pagando la caparra confermi la prenotazione. Il saldo dovrà essere versato in contanti o con carta al momento del ritiro del veicolo presso la nostra sede di Marciana Marina."}
          </p>
        </div>
      </div>

      {/* Customer profile (auto-shown if any required field missing) */}
      <div className="border border-zinc-800 rounded-xl p-6 bg-zinc-900/40" data-testid="checkout-customer-block">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-[#FFD700]" />
          <h2 className="font-semibold text-zinc-100">{isEN ? "Customer details" : "Dati del cliente"}</h2>
          {!profileComplete && (
            <span className="ml-auto text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full border border-red-500/30 inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {isEN ? "Incomplete" : "Incompleto"}
            </span>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {REQUIRED_USER_FIELDS.map((f) => (
            <div key={f.key} className={f.colSpan === 2 ? "sm:col-span-2" : ""}>
              <Label className="text-xs text-zinc-400">
                {(isEN ? f.labelEn : f.labelIt)} <span className="text-[#FFD700]">*</span>
              </Label>
              <Input
                type={f.type || "text"}
                value={profile[f.key] || ""}
                onChange={(e) => setProfile((p) => ({ ...p, [f.key]: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 mt-1"
                data-testid={`checkout-${f.key.replace(/_/g, "-")}-input`}
              />
            </div>
          ))}
        </div>
        <Button
          type="button"
          onClick={saveProfile}
          disabled={savingProfile || profileComplete}
          variant="outline"
          className="mt-4 bg-zinc-950 border-zinc-700 hover:bg-zinc-800 disabled:opacity-40"
          data-testid="checkout-save-profile-button"
        >
          {savingProfile ? (isEN ? "Saving…" : "Salvataggio…")
           : profileComplete ? (isEN ? "✓ Profile complete" : "✓ Profilo completo")
           : (isEN ? "Save profile" : "Salva profilo")}
        </Button>
      </div>

      {/* Document pickup notice */}
      <div className="rounded-xl border border-[#FFD700]/40 bg-[#FFD700]/5 p-4" data-testid="doc-pickup-notice">
        <div className="flex gap-3">
          <IdCard className="w-5 h-5 text-[#FFD700] shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-200 leading-relaxed">
            <span className="font-semibold text-[#FFD700]">{isEN ? "Document pickup:" : "Ritiro documento:"}</span>{" "}
            {isEN
              ? "When picking up the vehicle at our office, the customer must present a valid identity document, which will be photocopied front and back for contractual and administrative purposes."
              : "Al momento del ritiro del mezzo presso la sede, il cliente dovrà presentare un documento di identità valido, che verrà copiato fronte e retro per finalità contrattuali e amministrative."}
          </p>
        </div>
      </div>

      {/* Contract acceptance */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={accepted}
            onCheckedChange={setAccepted}
            id="checkout-accept"
            className="mt-1 border-zinc-700 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-zinc-950 data-[state=checked]:border-[#FFD700]"
            data-testid="checkout-accept-contract"
          />
          <label htmlFor="checkout-accept" className="text-sm text-zinc-200 leading-relaxed cursor-pointer select-none">
            <ShieldCheck className="w-4 h-4 inline mr-1 text-[#FFD700]" />
            {isEN ? (
              <>I declare to have read, understood and fully accepted the{" "}
                <Link to="/terms" target="_blank" className="text-[#FFD700] underline hover:brightness-110" data-testid="checkout-terms-link">vehicle rental contract</Link>{" "}of Rent Lupi.</>
            ) : (
              <>Dichiaro di aver letto, compreso e accettato integralmente il{" "}
                <Link to="/terms" target="_blank" className="text-[#FFD700] underline hover:brightness-110" data-testid="checkout-terms-link">contratto di locazione veicoli</Link>{" "}di Rent Lupi.</>
            )}{" "}
            <span className="text-[#FFD700]">*</span>
          </label>
        </div>
        <div className="flex items-start gap-3 pt-3 border-t border-zinc-800">
          <Checkbox
            checked={acceptedNoRefund}
            onCheckedChange={setAcceptedNoRefund}
            id="checkout-norefund"
            className="mt-1 border-zinc-700 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-zinc-950 data-[state=checked]:border-[#FFD700]"
            data-testid="checkout-accept-norefund"
          />
          <label htmlFor="checkout-norefund" className="text-sm text-zinc-200 leading-relaxed cursor-pointer select-none">
            <AlertCircle className="w-4 h-4 inline mr-1 text-amber-400" />
            {isEN
              ? <>I understand and accept that <strong className="text-amber-300">the deposit (25%) is non-refundable</strong> in case of cancellation, no-show, early return or force majeure (Art. 1385 c.c.).</>
              : <>Comprendo e accetto che <strong className="text-amber-300">la caparra confirmatoria (25%) è trattenuta integralmente</strong> in caso di cancellazione, mancato ritiro, restituzione anticipata o cause di forza maggiore (art. 1385 c.c.).</>}{" "}
            <span className="text-[#FFD700]">*</span>
          </label>
        </div>
      </div>

      {/* Maintenance banner */}
      {maintenance && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="payments-maintenance-banner">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-100">
            <p className="font-semibold mb-1">
              {isEN ? "Online payments temporarily unavailable" : "Pagamenti online temporaneamente non disponibili"}
            </p>
            <p className="text-amber-100/80">
              {isEN
                ? "We're updating our payment system. Your booking is saved — you can complete it shortly or contact us on WhatsApp +39 0565 904134 to pay directly."
                : "Stiamo aggiornando il sistema di pagamento. La tua prenotazione è salvata — potrai completarla a breve oppure contattaci su WhatsApp +39 0565 904134 per pagare direttamente."}
            </p>
          </div>
        </div>
      )}

      <Button onClick={pay} disabled={loading || booking.payment_status === "pagato" || !accepted || !acceptedNoRefund || !profileComplete || maintenance}
              className="w-full h-12 bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold text-base disabled:opacity-40"
              data-testid="pay-with-revolut-button">
        <Lock className="w-4 h-4 mr-2" />
        {booking.payment_status === "pagato"
          ? t("checkout.already_paid")
          : maintenance
            ? (isEN ? "Payments in maintenance" : "Pagamenti in manutenzione")
            : loading
              ? t("checkout.redirecting")
              : (isEN
                  ? `Pay deposit €${(booking.caparra_amount ?? booking.total_amount * 0.25).toFixed(2)} with Revolut`
                  : `Versa caparra €${(booking.caparra_amount ?? booking.total_amount * 0.25).toFixed(2)} con Revolut`)}
      </Button>

      <Button variant="ghost" onClick={() => navigate("/my-rentals")}
              className="w-full text-zinc-400 hover:text-zinc-100"
              data-testid="checkout-back-button">
        {t("checkout.back_to_rentals")}
      </Button>
    </div>
  );
}

function Row({ label, value, small }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-semibold ${small ? "text-sm" : ""}`}>{value}</span>
    </div>
  );
}
